import { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Check, ChevronDown, Search, Plus, ShieldCheck, Loader2 } from 'lucide-react';
import { searchManufacturers, suggestManufacturer } from '@/lib/api';

export const ManufacturerDropdown = ({ value, onChange, disabled }) => {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestName, setSuggestName] = useState('');
  const [suggestReason, setSuggestReason] = useState('');
  const [suggestSaving, setSuggestSaving] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync external value changes
  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 1) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await searchManufacturers(q);
      setResults(res.data || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setOpen(true);

    // Propagate the typed value to the parent form immediately so that
    // even free-text manufacturer names (without picking a suggestion)
    // survive validation/submit.
    onChange(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 200);
  };

  const handleSelect = (mfr) => {
    setQuery(mfr.name);
    onChange(mfr.name);
    setOpen(false);
  };

  const handleSuggest = async () => {
    if (!suggestName.trim()) { toast.error('Name ist erforderlich'); return; }
    setSuggestSaving(true);
    try {
      await suggestManufacturer(suggestName.trim(), suggestReason.trim() || null);
      toast.success('Vorschlag eingereicht! Admin wird benachrichtigt.');
      setSuggestOpen(false);
      setSuggestName('');
      setSuggestReason('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Einreichen');
    } finally {
      setSuggestSaving(false);
    }
  };

  const showNoResults = open && query.length >= 2 && !loading && results.length === 0;

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          value={query}
          onChange={handleInputChange}
          onFocus={() => { if (query.length >= 1) { setOpen(true); doSearch(query); } }}
          placeholder="Hersteller suchen..."
          className="pl-9 pr-8"
          disabled={disabled}
          data-testid="manufacturer-dropdown-input"
        />
        {loading && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
        {!loading && <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />}
      </div>

      {open && (results.length > 0 || showNoResults) && (
        <div className="absolute z-50 mt-1 w-full bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-y-auto"
          data-testid="manufacturer-dropdown-list">
          {results.map(mfr => (
            <button key={mfr.manufacturer_id} type="button"
              className="w-full text-left px-3 py-2 hover:bg-accent transition-colors flex items-center justify-between gap-2"
              onClick={() => handleSelect(mfr)}
              data-testid={`mfr-option-${mfr.manufacturer_id}`}>
              <div className="flex flex-col min-w-0">
                <span className="font-medium flex items-center gap-1.5 text-sm">
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                  {mfr.name}
                </span>
                {mfr.aliases?.length > 0 && (
                  <span className="text-xs text-muted-foreground truncate">
                    Auch bekannt als: {mfr.aliases.join(', ')}
                  </span>
                )}
              </div>
              {value === mfr.name && <Check className="h-4 w-4 text-emerald-500 shrink-0" />}
            </button>
          ))}

          {showNoResults && (
            <div className="px-3 py-3 text-center space-y-2">
              <p className="text-sm text-muted-foreground">Kein Hersteller gefunden</p>
              <Button size="sm" variant="outline" onClick={() => { setSuggestName(query); setSuggestOpen(true); setOpen(false); }}
                data-testid="suggest-manufacturer-btn">
                <Plus className="h-3.5 w-3.5 mr-1" /> "{query}" als neuen Hersteller vorschlagen
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Suggestion Dialog */}
      <Dialog open={suggestOpen} onOpenChange={setSuggestOpen}>
        <DialogContent className="max-w-md" data-testid="suggest-manufacturer-dialog">
          <DialogHeader>
            <DialogTitle>Neuen Hersteller vorschlagen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Hersteller-Name *</Label>
              <Input value={suggestName} onChange={e => setSuggestName(e.target.value)}
                placeholder="Exakter Hersteller-Name" data-testid="suggest-name-input" />
            </div>
            <div>
              <Label>Grund / Anmerkung</Label>
              <Textarea value={suggestReason} onChange={e => setSuggestReason(e.target.value)}
                placeholder="Warum benotigen Sie diesen Hersteller?" rows={2} data-testid="suggest-reason-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSuggestOpen(false)}>Abbrechen</Button>
              <Button onClick={handleSuggest} disabled={suggestSaving} data-testid="suggest-submit-btn">
                {suggestSaving ? 'Einreichen...' : 'Vorschlag einreichen'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
